import { useMemo } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type MapPaletteKey = 'classic' | 'satellite';

/** The colours a user may change from the legend. */
export type MapColors = {
  parcelLine: string;
  parcelFill: string;
  subparcel: string;
  photo: string;
};

export type MapColorKey = keyof MapColors;

export const MAP_COLOR_KEYS: readonly MapColorKey[] = [
  'parcelLine',
  'parcelFill',
  'subparcel',
  'photo',
];

type PaletteExtras = {
  parcelFillOpacity: number;
  /** Dark line under the outline so a bright edge stays sharp on light ground. */
  parcelHalo: string | null;
  /** Edge of the photo circle; white separates it from dark forest. */
  photoRing: string | null;
};

export type MapPalette = { colors: MapColors } & PaletteExtras;

// Yellow and magenta do not occur in ground cover, so on imagery they never
// blend into forest or ploughed fields the way green and orange do.
export const MAP_PALETTES: Record<MapPaletteKey, MapPalette> = {
  classic: {
    colors: {
      parcelLine: '#14532d',
      parcelFill: '#16a34a',
      subparcel: '#c2410c',
      photo: '#2563eb',
    },
    parcelFillOpacity: 0.2,
    parcelHalo: null,
    photoRing: null,
  },
  satellite: {
    colors: {
      parcelLine: '#facc15',
      parcelFill: '#facc15',
      subparcel: '#e879f9',
      photo: '#0ea5e9',
    },
    parcelFillOpacity: 0.08,
    parcelHalo: '#1f2937',
    photoRing: '#ffffff',
  },
};

export type MapColorOverrides = Partial<Record<MapPaletteKey, Partial<MapColors>>>;

export type ResolvedMapStyle = MapPalette & { palette: MapPaletteKey };

const HEX = /^#[0-9a-f]{6}$/i;

/** Pure: the palette with the user's valid overrides applied on top. */
export function resolveMapStyle(
  palette: MapPaletteKey,
  overrides: MapColorOverrides,
): ResolvedMapStyle {
  const key: MapPaletteKey = palette in MAP_PALETTES ? palette : 'classic';
  const base = MAP_PALETTES[key];
  const own = overrides[key] ?? {};
  const colors = { ...base.colors };
  for (const k of MAP_COLOR_KEYS) {
    const v = own[k];
    if (typeof v === 'string' && HEX.test(v)) colors[k] = v.toLowerCase();
  }
  return { ...base, colors, palette: key };
}

type MapColorState = {
  palette: MapPaletteKey;
  overrides: MapColorOverrides;
  setPalette: (p: MapPaletteKey) => void;
  setColor: (k: MapColorKey, v: string) => void;
  resetColors: () => void;
};

export const useMapColorStore = create<MapColorState>()(
  persist(
    (set) => ({
      palette: 'classic',
      overrides: {},
      setPalette: (palette) => set({ palette }),
      setColor: (k, v) =>
        set((s) => ({
          overrides: { ...s.overrides, [s.palette]: { ...s.overrides[s.palette], [k]: v } },
        })),
      resetColors: () =>
        set((s) => {
          const next = { ...s.overrides };
          delete next[s.palette];
          return { overrides: next };
        }),
    }),
    {
      name: 'forest.map.colors',
      partialize: (s) => ({ palette: s.palette, overrides: s.overrides }),
    },
  ),
);

export function useMapStyle(): ResolvedMapStyle {
  const palette = useMapColorStore((s) => s.palette);
  const overrides = useMapColorStore((s) => s.overrides);
  return useMemo(() => resolveMapStyle(palette, overrides), [palette, overrides]);
}
